import { useSignal } from '@preact/signals';
import { useEffect } from 'preact/hooks';

import type { AgendaEvent, EventRoleKind, OccurrenceRole } from '@amumin/api-client';
import { t } from '@amumin/i18n';
import { Button } from '@amumin/ui';
import { cx, Icon, MemberChip } from '@amumin/ui-core';

import { api } from '../api.js';
import type { MemberLite } from '../lib/agenda.js';
import { formatDateKeyShort, weekdayOf } from '../lib/dates.js';
import { householdPaused } from '../lib/paused.js';
import { mutate } from '../lib/ui.js';
import { appText, messages, ownMemberId } from '../state/session.js';
import styles from './RideTakeover.module.css';
import { DriverChoices, driversOf } from './RidesEditor.js';

// Eén keer overnemen (0022, verzoek oprichter: "voor die dag aanpassen als de een niet kan
// waardoor de ander het overneemt"; DESIGN.md §8.W2/W4). Op het afspraakblad en op de pagina
// Ritten: per rit duidelijk wie rijdt ("Brengen · Baba"), een vriendelijke knop "Kan niet?
// Iemand anders" met de andere rijders en "Nog niet bekend", en voor je eigen rit "Ik kan niet"
// (dan staat hij op "Wie rijdt?" en ziet de ander hem). Alles alleen voor deze keer (setDriver
// scope 'this'), met Ongedaan maken, "Alleen deze keer · normaal Baba" en "Terug naar normaal".

type Kind = 'take' | 'cant' | 'back';

/** Werkwoord voor de rol ("brengt" / "haalt"). */
function verb(role: EventRoleKind): string {
  const r = appText.value.ridesPerDay;
  return role === 'dropoff' ? r.verbDropoff : r.verbPickup;
}

// Per keer hooguit één melding met Ongedaan maken, ook over brengen en halen heen: brengen en
// halen van één keer delen één aanpassing (event_override) met één versie. Na een tweede actie op
// dezelfde keer (eerst brengen, dan halen, of "Terug naar normaal") hoort de oude Ongedaan maken
// bij een versie die niet meer bestaat en gaf hij een 409 met "door iemand anders gewijzigd",
// terwijl het je eigen actie was (review c-reg r1 en r2). De oude melding gaat al weg zodra de
// nieuwe actie begint, niet pas als die klaar is (review c-ux r3: tijdens het opslaan kon de oude
// Ongedaan maken nog). Eén ritmelding tegelijk, ook over keren heen: op Ritten stapelden twee
// meldingen over de volgende rij.
let rideToast: (() => void) | null = null;

function closeRideToast(): void {
  rideToast?.();
  rideToast = null;
}

/** Slaat de rijder van deze ene keer op, met een melding en Ongedaan maken. */
export async function saveRideOnce(
  ev: AgendaEvent,
  role: OccurrenceRole,
  memberId: string | null,
  members: ReadonlyArray<MemberLite>,
  kind: Kind,
  onUndone?: () => void,
): Promise<boolean> {
  const r = appText.value.ridesPerDay;
  const previous = role.member_id;
  const name = members.find((x) => x.id === memberId)?.display_name ?? '';
  const roleName =
    role.role === 'dropoff' ? appText.value.event.dropoff : appText.value.event.pickup;
  closeRideToast();
  const res = await mutate(
    () =>
      api.app.occurrences.setDriver(ev.occurrence_id, {
        role: role.role,
        member_id: memberId,
        scope: 'this',
        if_version: ev.override_version ?? 0,
      }),
    {
      success:
        kind === 'back'
          ? t(r.backDone, { role: roleName })
          : memberId
            ? t(r.takenOver, { name, role: verb(role.role) })
            : t(r.nobodyNow, { role: roleName }),
      undo: async () => {
        if (res) {
          await api.app.occurrences.setDriver(ev.occurrence_id, {
            role: role.role,
            member_id: previous,
            scope: 'this',
            if_version: res.override_version,
          });
          onUndone?.();
        }
      },
      onToast: (dismiss) => {
        closeRideToast();
        rideToast = dismiss;
      },
    },
  );
  return res !== null;
}

/** "Alleen deze keer · normaal Baba" (of "elke donderdag" bij een rijder per weekdag). */
export function RideSourceNote({
  ev,
  role,
  members,
}: {
  ev: AgendaEvent;
  role: OccurrenceRole;
  members: ReadonlyArray<MemberLite>;
}) {
  const r = appText.value.ridesPerDay;
  const m = messages.value;
  // Rijdt deze keer toch wie normaal rijdt (de regel is later zo gezet), dan zegt "Alleen deze
  // keer · normaal Baba" niets: dan tonen we de gewone bron (review c-fs/c-ux r1).
  const sameAsUsual =
    role.source === 'override' &&
    role.usual_member_id !== undefined &&
    role.usual_member_id === role.member_id;
  if (role.source === 'override' && ev.recurring && !sameAsUsual) {
    const usual =
      role.usual_member_id === undefined
        ? null
        : role.usual_member_id === null
          ? r.usuallyNobody
          : t(r.usually, {
              name: members.find((x) => x.id === role.usual_member_id)?.display_name ?? '',
            });
    return (
      <span class={styles.note} data-name="ride-only-this-time">
        <span class={cx('tag', 'tag-info')}>{r.onlyThisTime}</span>
        {usual ? <span class="muted small">{usual}</span> : null}
      </span>
    );
  }
  if (role.source === 'weekday' && ev.original_date) {
    const iso = weekdayOf(ev.original_date);
    return (
      <span class={cx(styles.note, 'muted small')}>
        {t(r.everyWeekday, { day: m.weekdays[iso - 1]?.toLowerCase() ?? '' })}
      </span>
    );
  }
  return null;
}

/**
 * Knoppen en keuzepaneel voor één rit van één keer. `layout = 'sheet'`: onder de rit op het
 * afspraakblad; `'row'`: in een rij van de pagina Ritten.
 */
export function RideTakeover({
  ev,
  role,
  members,
  onDone,
  compact = false,
}: {
  ev: AgendaEvent;
  role: OccurrenceRole;
  members: ReadonlyArray<MemberLite>;
  onDone?: () => void;
  compact?: boolean;
}) {
  const a = appText.value;
  const r = a.ridesPerDay;
  const m = messages.value;
  const me = ownMemberId.value;
  const choosing = useSignal(false);
  // Welke knop bezig is (de draaier staat op de knop die je aantikte, review c-ux r3).
  const busy = useSignal<Kind | 'pick' | null>(null);
  // Nieuwe stand van deze keer (eigen actie, live van de ander of herladen na een 409): het
  // keuzepaneel gaat dicht, zodat er nooit een oude keuze aangevinkt blijft (review c-fs r2).
  useEffect(() => {
    choosing.value = false;
  }, [ev.occurrence_id, role.role, ev.override_version, role.member_id]);
  // Mislukt zonder nieuwe stand (bv. geen verbinding): het paneel blijft open, maar de keuzes
  // beginnen weer leeg, zodat dezelfde keuze nog eens aantikken opnieuw verstuurt.
  const attempt = useSignal(0);

  const drivers = driversOf(members);
  const others = drivers.filter((x) => x.id !== role.member_id);
  const mine = me !== null && role.member_id === me;
  const canGoBack =
    ev.recurring &&
    role.source === 'override' &&
    role.usual_member_id !== undefined &&
    role.usual_member_id !== role.member_id;

  const save = async (memberId: string | null, kind: Kind, control: Kind | 'pick' = kind) => {
    busy.value = control;
    const ok = await saveRideOnce(ev, role, memberId, members, kind, onDone);
    busy.value = null;
    if (ok) {
      choosing.value = false;
      onDone?.();
    } else {
      attempt.value += 1;
    }
  };

  const roleWord = verb(role.role);
  const date = ev.date ?? ev.original_date ?? '';
  // 0035 (review g4 ux r2): gepauzeerd = alleen kijken; geen "Ik rijd" of "Kan niet".
  if (householdPaused()) return null;
  return (
    <div class={cx(styles.takeover, compact && styles.compact)}>
      <div class={styles.actions}>
        {role.member_id === null && me ? (
          <Button
            name="ride-i-drive"
            size="small"
            variant="primary"
            icon="car"
            busy={busy.value === 'take'}
            onPress={() => void save(me, 'take')}
          >
            {a.event.iDrive}
          </Button>
        ) : null}
        {mine ? (
          <Button
            name="ride-i-cant"
            size="small"
            busy={busy.value === 'cant'}
            onPress={() => void save(null, 'cant')}
          >
            {r.iCant}
          </Button>
        ) : null}
        <Button
          name={role.member_id === null ? 'ride-choose' : 'ride-cant-drive'}
          size="small"
          icon="users"
          busy={busy.value === 'pick'}
          onPress={() => (choosing.value = !choosing.value)}
        >
          {role.member_id === null ? a.event.chooseDriver : mine ? r.someoneElse : r.cantDrive}
        </Button>
        {canGoBack ? (
          <Button
            name="ride-back-to-usual"
            size="small"
            variant="ghost"
            icon="rotate-ccw"
            busy={busy.value === 'back'}
            onPress={() => void save(role.usual_member_id ?? null, 'back')}
          >
            {r.backToUsual}
          </Button>
        ) : null}
      </div>
      {choosing.value ? (
        <div class={styles.panel} data-name="ride-takeover-panel">
          <p class={styles.panelTitle}>
            <Icon id="car" size={18} />
            {t(r.whoInstead, { role: roleWord, date: date ? formatDateKeyShort(date, m) : '' })}
          </p>
          {ev.recurring ? <p class="muted small">{r.whoInsteadHint}</p> : null}
          <DriverChoices
            key={attempt.value}
            name={`takeover-${ev.occurrence_id}-${role.role}`}
            legend={t(r.whoInstead, {
              role: roleWord,
              date: date ? formatDateKeyShort(date, m) : '',
            })}
            hideLegend
            value={null}
            onChange={(choice) => void save(choice === 'open' ? null : choice, 'take', 'pick')}
            drivers={others}
            allowNone={false}
            allowOpen={role.member_id !== null}
            dataName="ride-takeover-choices"
          />
        </div>
      ) : null}
    </div>
  );
}

/** De ritten van één keer op het afspraakblad: "Brengen · Baba", met overnemen. */
export function SheetRides({
  ev,
  members,
  onDone,
}: {
  ev: AgendaEvent;
  members: ReadonlyArray<MemberLite>;
  onDone: () => void;
}) {
  const a = appText.value;
  if (ev.roles.length === 0) return null;
  return (
    <section class="sheet-section" data-name="event-rides">
      <h3 class="section-label">{a.event.rides}</h3>
      <ul class={styles.list}>
        {ev.roles.map((role) => {
          const driver = role.member_id ? members.find((x) => x.id === role.member_id) : null;
          return (
            <li
              key={role.role}
              class={cx(styles.ride, !driver && styles.rideOpen)}
              data-name={`sheet-ride-${role.role}`}
            >
              <div class={styles.rideHead}>
                <span class={styles.rideLabel}>
                  <Icon id="car" size={20} />
                  <strong>{role.role === 'dropoff' ? a.event.dropoff : a.event.pickup}</strong>
                  <span class="tabular muted">{role.at_hhmm}</span>
                </span>
                {driver ? (
                  <MemberChip
                    name={driver.display_name}
                    colorKey={driver.color_key}
                    icon={driver.avatar_icon}
                    size="sm"
                  />
                ) : (
                  <span class="ride-missing">{a.event.driverOpen}</span>
                )}
              </div>
              <RideSourceNote ev={ev} role={role} members={members} />
              {!ev.cancelled ? (
                <RideTakeover ev={ev} role={role} members={members} onDone={onDone} />
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
